import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { useMediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { MediaObject } from '@euler/model';
import { memo, ReactElement, useCallback, useMemo } from 'react';
import { FlatList, FlatListProps, ListRenderItem } from 'react-native';

function MediaListComponent<T>({
  renderItem,
  mapToMediaObject,
  ...props
}: Omit<FlatListProps<T>, 'renderItem'> & {
  mapToMediaObject: (item: T) => MediaObject;
  renderItem: (params: {
    item: T;
    media: MediaObject;
    index: number;
    onPress: (media: MediaObject) => void;
  }) => ReactElement | null;
}) {
  const medias = useMemo(
    () => (props.data as T[])?.map<MediaObject>(mapToMediaObject),
    [mapToMediaObject, props.data],
  );

  const {
    isImageViewerOpen,
    setIsImageViewerOpen,
    imageUrls,
    currentImageIndex,
    onMediaPress,
  } = useMediaCarousel(medias ?? []);

  const renderCell: ListRenderItem<T> = useCallback(
    ({ item, index }) => {
      const mediaObj = mapToMediaObject(item);
      return renderItem({
        item,
        media: mediaObj,
        index,
        onPress: media => {
          onMediaPress(media, index);
        },
      });
    },
    [mapToMediaObject, onMediaPress, renderItem],
  );

  return (
    <>
      <ImageViewer
        images={imageUrls}
        index={currentImageIndex}
        show={isImageViewerOpen}
        onDismiss={() => setIsImageViewerOpen(false)}
      />
      <FlatList {...props} renderItem={renderCell} />
    </>
  );
}

export const MediaList = memo(MediaListComponent) as typeof MediaListComponent;
